import React from 'react';
import {
	Box,
	FormLabel,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { DetailsAccordion } from '@/components/forms/fields/DetailsAccordion';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

// Placeholders the Markdown email workflow fills in itself, in any row
const AUTOMATIC_PLACEHOLDERS = [
	{ placeholders: '{{subject}}, {{subheader}}', becomes: "The email's subject and subheader" },
	{ placeholders: '{POLICY_PERIOD}, {POLICY_YEAR_OR_PERIOD}', becomes: 'The policy period' },
	{
		placeholders: '{CLIENT_NAME}, {CLIENT_FIRST_NAME}',
		becomes: "The client's name and first name",
	},
	{ placeholders: '{BROKER_NAME}, {BROKER_COMPANY}', becomes: 'Your name and brokerage' },
	{ placeholders: '{RENEWAL_DATE_OR_PLACEHOLDER}', becomes: "The deal's renewal date" },
	{ placeholders: '{TOTAL_PREMIUM}', becomes: '****, for you to fill in' },
];

const Code = ({ children }) => (
	<Box component='code' sx={{ fontFamily: 'monospace', fontSize: '0.95em' }}>
		{children}
	</Box>
);

const Section = ({ title, children }) => (
	<Stack spacing={0.5}>
		<Typography variant='subtitle2'>{title}</Typography>
		{children}
	</Stack>
);

const BulletList = ({ items }) => (
	<Box component='ul' sx={{ m: 0, pl: 2.5 }}>
		{items.map((item, index) => (
			<Typography component='li' variant='body2' key={index}>
				{item}
			</Typography>
		))}
	</Box>
);

/**
 * Explains how the Markdown email workflow uses an AI template: which rows the AI writes, which
 * placeholders are filled in automatically, and what to keep out of the template.
 */
export const AiTemplateInstructions = ({ expanded, onChange }) => (
	<DetailsAccordion
		title={
			<FormLabel component='span' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<InfoOutlineIcon fontSize='small' />
				How AI emails use this template
			</FormLabel>
		}
		expanded={expanded}
		onChange={onChange}
		sx={{
			// DetailsAccordion leaves a gap below its title; this one sits flush in its row
			'& .MuiAccordionSummary-root': { mb: 0, minHeight: '32px' },
			'& .MuiAccordionSummary-content': { m: 0 },
		}}
	>
		<Stack
			spacing={2}
			sx={{
				py: 1,
				pl: '28px',
				// Body text and the table are smaller than the section titles
				'& .MuiTypography-body2, & .MuiTableCell-root': { fontSize: '0.75rem' },
			}}
		>
			<Section title='Mark where the AI writes'>
				<BulletList
					items={[
						<>
							Put a placeholder in capitals and curly braces, such as{' '}
							<Code>{'{CLIENT_OVERVIEW}'}</Code>, in each row the AI should write. The
							name is up to you.
						</>,
						'Placeholder rows next to each other, plus any heading-only rows between them, form one editable area. The AI’s content replaces that whole area.',
						'Headings inside an editable area tell the AI which sections to write, and in what order. Use the exact heading text you want in the email.',
						'The AI’s headings and tables copy the style of the headings and tables in the area.',
						'Every other row (logo, banner, greeting, fixed wording, sign-off, disclosures, footer) is sent exactly as designed.',
						'If the template has no placeholder rows, or only one on its own, the AI’s content goes above the footer instead.',
					]}
				/>
			</Section>

			<Section title='Filled in automatically'>
				<Table size='small' sx={{ '& .MuiTableHead-root': { height: 'unset' } }}>
					<TableHead>
						<TableRow>
							<TableCell>Placeholder</TableCell>
							<TableCell>Becomes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{AUTOMATIC_PLACEHOLDERS.map(({ placeholders, becomes }) => (
							<TableRow key={placeholders}>
								<TableCell>
									<Code>{placeholders}</Code>
								</TableCell>
								<TableCell>{becomes}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<Typography variant='body2'>
					Merge tags such as <Code>{'{{client.name}}'}</Code>,{' '}
					<Code>{'{{user.name}}'}</Code>, <Code>{'{{brokerage.name}}'}</Code> and{' '}
					<Code>{'{{deal.renewalDate}}'}</Code> are filled in when the email is sent.
				</Typography>
			</Section>

			<Section title='Avoid'>
				<BulletList
					items={[
						'Sample client details (names, addresses, policy numbers, schedules) in rows without a placeholder: they would be sent to every client.',
						'Placeholders in fixed wording you want kept, such as disclosures. The AI would replace that row.',
						'Other placeholders in the email’s preview text or accessibility title (in the editor’s body settings). Only the automatic ones above work there.',
					]}
				/>
			</Section>
		</Stack>
	</DetailsAccordion>
);
